import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { listEligibleHospitals } from "@/lib/api";
import { distanceKm } from "@/lib/gameConfig";
import { Buildings, Star, ShieldCheck, X } from "@phosphor-icons/react";

export default function TransportDialog({ open, onOpenChange, patient, call, medic, onPick }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listEligibleHospitals()
      .then((d) => setList(d.hospitals || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!patient || !call) return null;

  const sorted = (list || [])
    .map((h) => {
      const dist = distanceKm({ lat: h.lat, lng: h.lng }, { lat: call.lat, lng: call.lng });
      const minutes = Math.round(dist / 60 * 60); // ~60 km/h ⇒ 1 km per minute
      const specMatch = patient.specialty && (h.specialties || []).includes(patient.specialty);
      return { ...h, dist, minutes, specMatch, freeBeds: h.free_beds };
    })
    .sort((a, b) => {
      // specialty match first, then free beds desc, then nearest
      if (a.specMatch !== b.specMatch) return a.specMatch ? -1 : 1;
      if ((b.freeBeds || 0) !== (a.freeBeds || 0)) return (b.freeBeds || 0) - (a.freeBeds || 0);
      return a.dist - b.dist;
    });

  const transport = async (h) => {
    if (!onPick) return;
    await onPick(h);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="transport-dialog"
        className="rounded-none border border-white/10 bg-[#0F1115] text-white max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="mono text-[12px] uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2">
            <Buildings size={14} className="text-pink-400" /> Choose Destination Hospital
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-3">
          <div className="border border-white/10 px-3 py-2">
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Patient</div>
            <div className="text-[12px] font-bold">
              {patient.specialty ? (
                <>
                  <span className="text-amber-300">{patient.specialty} required</span>
                </>
              ) : (
                <span className="text-zinc-300">Standard transport</span>
              )}
            </div>
          </div>

          {loading && <div className="mono text-[10px] uppercase text-zinc-500 py-3 text-center">Searching…</div>}

          {!loading && sorted.length === 0 && (
            <div className="mono text-[10px] uppercase text-zinc-500 py-3 text-center">
              No hospitals available — build one or wait for a bed to free up
            </div>
          )}

          {sorted.map((h) => {
            const noBeds = (h.freeBeds ?? 0) <= 0;
            return (
              <div
                key={h.id}
                data-testid={`hospital-row-${h.id}`}
                className={`border px-3 py-2 flex items-center gap-2 ${
                  noBeds ? "border-zinc-700 opacity-50" : h.specMatch ? "border-emerald-400/60 bg-emerald-400/5" : "border-white/10 hover:bg-white/5"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-bold tracking-tight flex items-center gap-2">
                    {h.name}
                    {h.kind === "alliance" && (
                      <span className="mono text-[8px] uppercase tracking-widest px-1 py-px border border-pink-400/40 text-pink-300">
                        ALLIANCE · {h.alliance_name} · FEE {h.alliance_fee_pct || 0}%
                      </span>
                    )}
                    {h.kind === "player_shared" && (
                      <span className="mono text-[8px] uppercase tracking-widest px-1 py-px border border-cyan-400/40 text-cyan-300">
                        {h.owner_name} · FEE {h.owner_fee_pct || 0}%
                      </span>
                    )}
                  </div>
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-400 mt-0.5">
                    {h.dist.toFixed(1)} km · {h.minutes} min · {h.freeBeds}/{h.beds} beds free
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {(h.specialties || []).length === 0 ? (
                      <span className="mono text-[8px] uppercase tracking-widest text-zinc-600">no specialties</span>
                    ) : (
                      (h.specialties || []).map((s) => (
                        <span
                          key={s}
                          className={`mono text-[8px] uppercase tracking-widest px-1 py-px border ${
                            patient.specialty === s
                              ? "border-emerald-400 text-emerald-300 bg-emerald-400/10"
                              : "border-zinc-600 text-zinc-400"
                          }`}
                        >
                          <Star size={7} weight="fill" className="inline mr-0.5" />{s}
                        </span>
                      ))
                    )}
                  </div>
                </div>
                {h.specMatch && (
                  <span title="Specialty match — +$1000" className="text-emerald-400">
                    <ShieldCheck size={16} weight="fill" />
                  </span>
                )}
                <Button
                  data-testid={`transport-pick-${h.id}`}
                  disabled={noBeds}
                  onClick={() => transport(h)}
                  className="rounded-none h-7 mono uppercase text-[9px] tracking-widest bg-pink-500 hover:bg-pink-400 text-black disabled:opacity-30"
                >
                  {noBeds ? "Full" : "Transport"}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-2">
          <Button
            data-testid="transport-cancel"
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="rounded-none h-7 mono uppercase text-[9px] tracking-widest border-white/15"
          >
            <X size={10} className="mr-1" /> Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
